import type { ImageDecodeOutcome } from '@dutchie/capacitor-hardware';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { ScreenLoader } from '@dutchie-pos/components';
import {
  ID_SCAN_ENTRY_FAILURE_MESSAGE,
  ID_SCAN_NO_DECODER_MESSAGE,
  ID_SCAN_UNKNOWN_MESSAGE,
  type ScanGuestEntryResponse,
  type ScanValidatedCustomer,
  UPLOAD_NO_BARCODE_MESSAGE,
  UPLOAD_UNSUPPORTED_MESSAGE,
  decodeScanEntry,
  frontPhotoTarget,
  needsFrontIdPhotoForCandidate,
  newProfileNeedsFrontIdPhoto,
  photoSaveFailureMessage,
  toIdPhotoPayload,
} from '@dutchie-pos/requests/checkin/camera-id-scan';
import { needsFrontIdPhoto } from '@dutchie-pos/requests/checkin/id-photo-presence';
import type { IdDocumentType, IdFieldName, IdFieldValues } from '@dutchie-pos/requests/checkin/typed-id-entry';
import { missingRequiredFields, selectCustomerTypeId, toLocalDate } from '@dutchie-pos/requests/checkin/typed-id-entry';
import { isMmjIdExpired } from '@dutchie-pos/utils';

import {
  ScanEntryRefusedError,
  useCameraScanEntryMutation,
  useIdPhotoMutation,
} from 'src/queries/curbside/camera-id-scan';
import type { DuplicateGuestMatch } from 'src/queries/curbside/guest-entry';
import {
  toTypedGuestCreateBody,
  useCheckDuplicateGuestMutation,
  useCheckInGuestMutation,
  useCreateGuestMutation,
  useCreateTypedGuestMutation,
} from 'src/queries/curbside/guest-entry';
import { useLocation } from 'src/queries/curbside/location-details';
import { useCustomerTypesQuery } from 'src/queries/posv3/maintenance/get-customer-types';
import { errorNotification, successNotification } from 'src/store/actions/notifications-actions';
import { useAppDispatch, useAppSelector } from 'src/utils';
import { customEventKeys, logger } from 'src/utils/logger';
import type { IdCameraScanContext, IdPhotoSaveFailureContext } from 'src/utils/logger/types/scanning';

import { ContentLayout } from '../../components/content-layout';
import { ContentTitle } from '../../components/content-title';
import { headerHeight } from '../../components/header';
import { CompletionStep } from './completion-step';
import { DecisionStep } from './decision-step';
import { FrontPhotoStep } from './front-photo/front-photo-step';
import { readIdPhoto } from './front-photo/id-photo';
import { type CameraScanResult, CaptureStep } from './scan/capture-step';
import { useCameraIdScanAccess } from './scan/use-camera-id-scan-access';

const NOT_AN_IMAGE_MESSAGE = 'Choose a photo of the ID.';
const UNRESOLVED_MESSAGE = 'This ID could not be matched to a profile. Check the guest in from the register.';
const CREATE_FAILURE_MESSAGE = 'The profile could not be created.';
// Kept apart from the create failure: once the profile exists, "could not be created" invites the
// retry that duplicates it.
const CHECK_IN_FAILURE_MESSAGE = 'The customer could not be checked in.';
const DUPLICATE_CHECK_FAILURE_MESSAGE = 'We could not check for an existing customer. Try again.';

/**
 * What a typed entry is refused for, and every one of them ends at the register.
 *
 * Only things this screen can actually compute from what staff typed plus the location's own
 * settings: an age below this location's threshold, an identification the location requires to be
 * valid and current, and a medical card at a location whose configuration names no medical customer
 * type. The profile form's full required-field policy is not consulted here — this app has no client
 * for it — so a refusal never claims more than it checked, and hands the customer to the surface
 * that does have it.
 */
const REGISTER_REFERRAL = 'Check the guest in from the register.';
const TYPED_MINOR_MESSAGE = `This date of birth is under the age this location allows. ${REGISTER_REFERRAL}`;
const TYPED_INVALID_ID_MESSAGE = `This location needs a current, unexpired ID. ${REGISTER_REFERRAL}`;
const TYPED_EXPIRED_MEDICAL_MESSAGE = `This medical card has expired. ${REGISTER_REFERRAL}`;
/**
 * Said only for a settled list that genuinely does not determine one — a location with several
 * medical types and no medical default, or none at all. It deliberately does not claim the location
 * configured nothing: the shared selector answers `undefined` for more than one reason, and only
 * this one is about configuration.
 */
const TYPED_UNDETERMINED_TYPE_MESSAGE = `This location's customer types do not determine one for a medical card. ${REGISTER_REFERRAL}`;
/**
 * The other two reasons, which are not facts about the location at all. Neither writes anything — a
 * create with no type resolved would take the backend's default instead of this location's answer,
 * silently, for either document — and the failed one starts a fresh request as it says so, because
 * this app's query client does not retry and a message that says "try again" has to mean it.
 */
const TYPED_TYPES_LOADING_MESSAGE = "Still loading this location's customer types. Try again in a moment.";
const TYPED_TYPES_FAILED_MESSAGE = "We could not load this location's customer types. Try again.";

/** Whole years, as an age is counted — not a millisecond division that rounds a birthday forward. */
const ageInYears = (dateOfBirth: Date, today: Date): number => {
  const years = today.getFullYear() - dateOfBirth.getFullYear();
  const hasHadBirthday =
    today.getMonth() > dateOfBirth.getMonth() ||
    (today.getMonth() === dateOfBirth.getMonth() && today.getDate() >= dateOfBirth.getDate());

  return hasHadBirthday ? years : years - 1;
};

/**
 * The server checked the customer in, or it handed staff a choice — and either way, once a customer
 * definitely exists and the response says that profile has no ID photo, the front of the card is
 * photographed. Nobody is asked to opt in: the response already knows who was checked in and
 * whether they have one, and a box staff could forget was the reason the photo was missing for the
 * customers who most needed it.
 *
 * There is deliberately no hand-entry step. The typed route this screen used to fall back to was
 * removed server-side (leaflogix #22416), so a decode that fails is answered the way the hardware
 * scanner answers one — the error, with retry and upload both still on the capture step, and the
 * back button out to the register chooser.
 */
type Step =
  | { name: 'capture' }
  /**
   * Hand entry, reached only when a barcode would not decode.
   *
   * `match` is a profile the duplicate check found for what was typed. It replaces the form rather
   * than sitting beside it: the endpoint answers with one record, so there is nothing to choose
   * between, and the only action offered is checking that person in. Creating a second profile from
   * here is the mistake the check exists to catch.
   *
   * `createdPendingCheckIn` is the other thing that replaces the form: a profile this screen itself
   * just created, whose check-in then failed. Fixed the moment the create lands — the id, and the
   * MJ id the create body actually carried — so a retry from here can only be "check this exact
   * profile in again", never a second create and never a check-in built from whatever staff have
   * since typed over it. Nothing editable is offered once this is set; Scan again is still the way
   * out, for a different customer.
   */
  | {
      name: 'completion';
      values: IdFieldValues;
      match: DuplicateGuestMatch | null;
      createdPendingCheckIn: { Guest_id: number; MJStateIDNo: string | null } | null;
    }
  | {
      name: 'decision';
      /**
       * The response these candidates came from, kept because a candidate alone cannot say what kind
       * of profile it is: the one the pipeline created is only recognisable against the response's
       * `CreatedCustomerId`, and that is what decides which presence default applies to it.
       */
      entry: ScanGuestEntryResponse | null;
      candidates: ScanValidatedCustomer[];
      createFrom: ScanValidatedCustomer | null;
      identified: ScanValidatedCustomer | null;
    }
  // The check-in already happened; this is the automatic front photo for a customer who definitely
  // exists and, by the response's own answer, has no photo yet. `isSaving` lives on the step because
  // the controls are disabled by a prop, and it is true for the whole persist — including the
  // compression that runs long before the mutation is pending.
  | { name: 'frontPhoto'; customerId: number; confirmation: string; isSaving: boolean };

// The one error mapper on this screen. `post` interpolates an absent `Message`, so a refusal with nothing to say
// arrives as the literal "null" — which is no message, and never a real one.
const message = (error: unknown, fallback: string): string => {
  const raw = error instanceof Error ? error.message : error;
  const text = typeof raw === 'string' ? raw.trim() : '';

  return text && text !== 'null' && text !== 'undefined' ? text : fallback;
};

export const CheckIn = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const registerId = useAppSelector((state) => state.user.selectedRegister?.id);
  const { data: location, isLoading: isLoadingLocation } = useLocation();
  const isCameraIdScanEnabled = useCameraIdScanAccess();

  const [step, setStep] = useState<Step>({ name: 'capture' });
  /**
   * Which document staff say they are holding. Chosen on the capture step, before the scan, and
   * read only by the typed fallback: a decode needs no help naming its own fields, but a typed
   * number has to be put in the licence slot or the medical one, and only the person holding the
   * card knows which.
   */
  const [documentType, setDocumentType] = useState<IdDocumentType>('DriverLicense');
  // The same fact as the step's `isSaving`, in a ref: the guard has to hold within the tick a second
  // tap lands in, before any re-render. Skip and a capture used to both finish, one confirming twice.
  const isSavingPhoto = useRef(false);
  /**
   * Whether the parked completion has already run. Latched, and reset only when a new completion is
   * parked, so every exit from the photo step — a shutter, an upload, Skip, a compressor that throws
   * — settles it exactly once. Starts true because nothing is parked yet.
   */
  const isPhotoSettled = useRef(true);
  // The profile `v2/guest/create` made. A ref, so the guard holds within the tick a second tap lands in.
  const createdCustomerId = useRef<number | null>(null);
  // Which decision is current, bumped whenever one ends. Both exits are reachable while a write is in
  // flight, and a late create whose id was kept made the next decision's Create check in the previous
  // profile under this customer's details.
  const decisionGeneration = useRef(0);
  // Whether staff are still on this screen. A write settles after any exit, and `navigate` still works
  // once this is unmounted, so its completion would otherwise pull staff back from wherever they went.
  const isMounted = useRef(true);
  /**
   * The typed form's own in-flight latch, acquired synchronously before `handleSubmitTyped`'s first
   * await. `isSubmitting` below is state derived from the mutations' own `isPending` flags, which do
   * not flip until React processes the update each mutation schedules — so two taps landing in the
   * same tick both read it as `false` and each would reach the duplicate check and the create. The
   * state stays, because it is what the button's `disabled` prop reads; this ref closes the window
   * state cannot see.
   */
  const isSubmittingRef = useRef(false);

  const cameraScan = useCameraScanEntryMutation();
  const saveIdPhoto = useIdPhotoMutation();
  const checkInGuest = useCheckInGuestMutation();
  const createGuest = useCreateGuestMutation();
  const checkDuplicateGuest = useCheckDuplicateGuestMutation();
  const createTypedGuest = useCreateTypedGuestMutation();
  // Only the screen that can type a card needs the location's customer types, so the request waits
  // for the step that reads them.
  const {
    data: customerTypes,
    isPending: isLoadingCustomerTypes,
    isError: hasCustomerTypesFailed,
    refetch: refetchCustomerTypes,
  } = useCustomerTypesQuery({ enabled: step.name === 'completion' });

  const isSubmitting =
    checkInGuest.isPending || createGuest.isPending || checkDuplicateGuest.isPending || createTypedGuest.isPending;

  // Set on every run, not only torn down: StrictMode remounts every effect, so a cleanup-only version
  // left this false for the screen's life and every `finish` stopped after its toast.
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  /** Back to the camera, and the end of the decision that was in progress. */
  const returnToCapture = () => {
    decisionGeneration.current += 1;
    createdCustomerId.current = null;
    setStep({ name: 'capture' });
  };

  /** False once the decision this write belongs to has been left behind. */
  const isCurrentDecision = (generation: number) => decisionGeneration.current === generation;

  // A write that settles after staff moved on — a stale decision, or a left screen — still confirms,
  // because it did happen; only the step reset and the navigation are withheld.
  const finish = (confirmation: string, isCurrent = true) => {
    void dispatch(successNotification(confirmation));

    if (!isCurrent || !isMounted.current) {
      return;
    }

    returnToCapture();
    navigate('/paths');
  };

  /**
   * Asks for the front photo before finishing, or finishes now.
   *
   * Two conditions now that the opt-in is gone, and both are load-bearing. `isCurrent` keeps a write
   * that settled after staff moved on from parking anything in front of the next customer. And
   * `photoCustomerId` is a profile that definitely exists *and* definitely needs a photo — null on
   * the decision path where the candidates are still guesses, null when the check-in landed on
   * somebody the server's own match list does not name, and null whenever presence is unknown or
   * already answered. A photo written to a customer staff then reject sits on the wrong record, and
   * a photo written over an existing one replaces a good scan-time image; neither is visible or
   * recoverable, so every caller resolves its own target through F4 and passes null rather than
   * guessing here.
   */
  const completeWith = (isCurrent: boolean, photoCustomerId: number | null, confirmation: string) => {
    if (isCurrent && photoCustomerId) {
      isPhotoSettled.current = false;
      isSavingPhoto.current = false;
      setStep({ name: 'frontPhoto', customerId: photoCustomerId, confirmation, isSaving: false });
      return;
    }

    finish(confirmation, isCurrent);
  };

  /**
   * The parked completion, run exactly once — and only for an outcome that ends the task: the photo
   * saved, or staff chose to skip it. A failed attempt leaves it unspent, because the step stays.
   */
  const settlePhoto = (confirmation: string) => {
    if (isPhotoSettled.current) {
      return;
    }

    isPhotoSettled.current = true;
    finish(confirmation);
  };

  /**
   * Never at the cost of the check-in that already happened.
   *
   * Returns whether the image is actually on the profile. Every other answer — a photo of the back,
   * a file that is not an image, a refusal with any other code, a save that threw — is a failed
   * attempt rather than the end of the task, and the caller keeps the step open for another try.
   */
  const persistPhoto = async (customerId: number, file: File): Promise<boolean> => {
    const notSaved = (failure: string | null | undefined) => {
      logger.info<IdPhotoSaveFailureContext>('ID photo not saved after entry', {
        key: customEventKeys.scanning.idPhotoSaveFailed,
        failure: failure ?? null,
      });
      dispatch(errorNotification(photoSaveFailureMessage(failure)));
    };

    // Caught here rather than in the caller's try/finally: `browser-image-compression` rejects on an
    // image it cannot decode, and that escaped as an unhandled rejection with no toast and no
    // telemetry. `undefined` comes only from this catch, distinct from a readable non-image's null.
    const captured = await readIdPhoto(file).catch(() => undefined);
    if (captured === undefined) {
      notSaved(null);
      return false;
    }
    if (!captured) {
      dispatch(errorNotification(NOT_AN_IMAGE_MESSAGE));
      return false;
    }

    try {
      const result = await saveIdPhoto.mutateAsync(toIdPhotoPayload(customerId, captured));
      // Only an explicit `Persisted: true` is the image being on the profile.
      //
      // A refused image is not a failed request — the server declines the back of a card this way,
      // and that is the photo failure staff can actually fix. The optional chain covers the other
      // half: an answer with no payload at all, null, undefined, or a body that did not
      // deserialise, is not evidence the image landed either, and it used to fall straight through
      // to `return true`. That reported a saved photo, settled the parked completion, navigated to
      // /paths and confirmed the check-in — with nothing on the profile and no way for staff to
      // tell. Treated as a failed attempt it stays on the step and retryable, which is what
      // Register's `useFrontIdPhoto` does.
      if (!result?.Persisted) {
        notSaved(result?.Failure ?? null);
        return false;
      }

      return true;
    } catch {
      notSaved(null);
      return false;
    }
  };

  const handleFrontPhoto = (file: File) => {
    if (step.name !== 'frontPhoto' || isSavingPhoto.current || isPhotoSettled.current) {
      return;
    }

    const { customerId, confirmation } = step;
    isSavingPhoto.current = true;
    setStep({ ...step, isSaving: true });

    void (async () => {
      // Starts false, so a `persistPhoto` that somehow threw leaves staff on a usable step rather
      // than confirming a photo that is not there.
      let isSaved = false;
      try {
        isSaved = await persistPhoto(customerId, file);
      } finally {
        isSavingPhoto.current = false;
        if (isSaved || !isMounted.current) {
          // Saved, or nobody is here to retry.
          //
          // The unmounted case is why the photo's outcome does not decide this on its own: the
          // check-in landed, and staff are owed that confirmation whether or not the optional image
          // followed it. Keeping the completion unspent would have thrown the confirmation away for
          // the one failure staff cannot see the step for. `finish` emits it and then stops short of
          // the reset and the navigation, so an operator who has moved on is told what happened
          // without being pulled back.
          settlePhoto(confirmation);
          return;
        }

        // Still here, and the attempt failed — every one of those failures is worth another try: a
        // photo of the back, a file that is not an image, a save that did not land. The copy for the
        // first of those literally says "Photograph the front to save it", so finishing here would
        // ask staff for something and then take the screen away before they could do it. Skip is
        // still the way out, and the check-in is already done either way.
        setStep((current) => (current.name === 'frontPhoto' ? { ...current, isSaving: false } : current));
      }
    })();
  };

  // Skip is first-class — the check-in already happened — and after a failed attempt it is the way
  // out. Refused while a photo is in flight: that persist decides its own outcome, so finishing here
  // too would confirm twice. Checked against the ref, so a tap in the same tick as a capture is caught.
  const handleSkipFrontPhoto = () => {
    if (step.name !== 'frontPhoto' || isSavingPhoto.current) {
      return;
    }

    settlePhoto(step.confirmation);
  };

  /**
   * Routes the server's answer, and nothing else. `AutoCreateWithIdScan` and `AutoCheckInWithIdScan`
   * arrive in the location payload this screen already reads, and are deliberately never consulted:
   * the response says what actually happened, so the shapes those settings produce are routed rather
   * than predicted.
   */
  const resolveEntry = (entry: ScanGuestEntryResponse | null, generation: number) => {
    const outcome = decodeScanEntry(entry);
    const isCurrent = isCurrentDecision(generation);

    if (isCurrent) {
      // Every resolved entry starts a new decision, so nothing created for the previous one carries in.
      decisionGeneration.current += 1;
      createdCustomerId.current = null;
    }

    // Confirmed even for a decision staff have left: the server checked this customer in either way.
    //
    // The photo target is F4's `frontPhotoTarget`, not this outcome's `photoCustomerId`. That field
    // is attribution only — it says who the entry landed on — while the target also carries the
    // server's `ResolvedCustomerId` anchor and the presence answer, and refuses a banned, unlisted
    // or disagreed-about record. Register's camera step resolves it the same way, from the same
    // function, so the two surfaces cannot drift on which customer gets photographed.
    if (outcome.kind === 'checkedIn') {
      completeWith(isCurrent, frontPhotoTarget(entry), 'Customer checked in');
      return;
    }
    // A late entry must not put its candidates in front of staff looking at the next scan.
    if (!isCurrent) {
      return;
    }
    if (outcome.kind === 'decision') {
      setStep({
        name: 'decision',
        entry,
        candidates: outcome.candidates,
        createFrom: outcome.createFrom,
        identified: outcome.identified,
      });
      return;
    }

    dispatch(errorNotification(UNRESOLVED_MESSAGE));
  };

  const enterWithBarcode = (payload: string) => {
    const generation = decisionGeneration.current;

    void (async () => {
      try {
        resolveEntry(await cameraScan.mutateAsync({ ID: payload, RegisterId: registerId ?? 0 }), generation);
      } catch (error) {
        // Only a `Result: false` refusal is the server saying no and saying why, and that reason is
        // the one thing staff can act on. Everything else — a success envelope with no payload, no
        // envelope at all, a transport failure — may still have checked someone in, so it gets the
        // guest-list caution instead of a reason it cannot honestly give.
        dispatch(
          errorNotification(
            error instanceof ScanEntryRefusedError
              ? message(error, ID_SCAN_UNKNOWN_MESSAGE)
              : ID_SCAN_ENTRY_FAILURE_MESSAGE,
          ),
        );
      }
    })();
  };

  /** One scan session, reported either way: how often real cards defeat the decoder is the metric. */
  const handleScanResult = ({ barcode, telemetry }: CameraScanResult) => {
    logger.info<IdCameraScanContext>('ID camera scan result', {
      key: customEventKeys.scanning.idCameraScanResult,
      source: 'camera',
      decoder: telemetry.decoder,
      decoded: barcode != null,
      frames: telemetry.frames,
      elapsedMs: telemetry.elapsedMs,
      roiWidthPx: telemetry.roiWidthPx,
    });

    if (!barcode) {
      // No decoder at all is a different problem from a barcode that would not read, and only one of
      // them is worth telling the operator to try again. Either way the card in front of staff did
      // not read, so hand entry opens behind the message rather than leaving a queue with no path.
      dispatch(errorNotification(telemetry.decoder ? ID_SCAN_UNKNOWN_MESSAGE : ID_SCAN_NO_DECODER_MESSAGE));
      revealTypedForm();
      return;
    }

    enterWithBarcode(barcode.text);
  };

  /** A picked still, decoded client-side. Reported on its own key so camera yield stays comparable. */
  const handleUploadResult = (result: ImageDecodeOutcome) => {
    logger.info<IdCameraScanContext>('ID upload scan result', {
      key: customEventKeys.scanning.idUploadScanResult,
      source: 'upload',
      decoder: result.decoder,
      decoded: result.barcode != null,
      frames: 1,
      elapsedMs: result.elapsedMs,
      roiWidthPx: result.widthPx,
    });

    if (result.barcode) {
      enterWithBarcode(result.barcode.text);
      return;
    }

    if (result.unsupported) {
      dispatch(errorNotification(UPLOAD_UNSUPPORTED_MESSAGE));
      revealTypedForm();
      return;
    }

    dispatch(errorNotification(result.decoder ? UPLOAD_NO_BARCODE_MESSAGE : ID_SCAN_NO_DECODER_MESSAGE));
    revealTypedForm();
  };

  /** Opens hand entry, unless staff are already in it: reopening would discard what they typed. */
  const revealTypedForm = () => {
    setStep((current) =>
      current.name === 'completion'
        ? current
        : { name: 'completion', values: {}, match: null, createdPendingCheckIn: null },
    );
  };

  const handleTypedFieldChange = (field: IdFieldName, value: string) => {
    setStep((current) =>
      current.name === 'completion' ? { ...current, values: { ...current.values, [field]: value } } : current,
    );
  };

  /**
   * What this screen can honestly refuse a typed entry for, computed from what was typed and this
   * location's own settings — and nothing else. Null means nothing computable stands in the way.
   *
   * Every refusal ends at the register, which is the surface with the full required-field policy.
   * A threshold this location has not configured is not a gate: it is an unknown, and inventing one
   * would refuse customers a configured location would admit.
   */
  const typedEntryRefusal = (values: IdFieldValues, customerTypeId: number | undefined): string | null => {
    /**
     * The list first, and for either document.
     *
     * `selectCustomerTypeId` answers `undefined` for three different reasons — the list has not
     * arrived, the request failed, or the configuration genuinely does not determine one — and only
     * the third is something to tell staff about their location. Folding the first two into that
     * sentence states something false, and this screen's refusals are only worth anything if they
     * describe what was actually checked.
     */
    if (isLoadingCustomerTypes) {
      return TYPED_TYPES_LOADING_MESSAGE;
    }
    /**
     * A failed load with nothing usable in hand. Two things matter here.
     *
     * The tap starts the retry: `retry: false` is this app's own query default and nothing else on
     * this screen refetches, so without this the query sat in error for as long as the step was
     * mounted — every Continue repeating a promise of a retry that never happened, with Scan again,
     * which discards everything typed, as the only way out.
     *
     * And `!customerTypes` is what keeps a *background* failure from refusing anything: with
     * `refetchOnWindowFocus` on, a failed refresh sets the error flag while the loaded list is still
     * there, and a list that is there is a list this can resolve from.
     */
    if (hasCustomerTypesFailed && !customerTypes) {
      void refetchCustomerTypes();

      return TYPED_TYPES_FAILED_MESSAGE;
    }

    // Settled, and it does not determine one. A medical card cannot be created as one without it,
    // and the check-in that would follow refuses a profile with no customer type on record. A
    // licence continues with the key omitted, which is the approved policy — but only from here,
    // where the location's own list was actually consulted.
    if (documentType === 'MedicalCard' && customerTypeId === undefined) {
      return TYPED_UNDETERMINED_TYPE_MESSAGE;
    }

    const settings = location?.Settings;
    const dateOfBirth = toLocalDate(values.DateOfBirth);
    /**
     * The document's own setting, then the other one — Register's rule, from `getMinorAge`: a
     * location that configured only a recreational minor age still has an age for a medical guest,
     * and refusing one this counter would admit is a different answer to the same question.
     *
     * `OfAge` stays as this app's last resort, and an unconfigured location still has no gate at
     * all: that is deliberate here, because this screen's refusal is "check them in at the
     * register", and inventing a threshold nobody configured would send staff away for nothing.
     */
    const minorAge =
      documentType === 'MedicalCard'
        ? (settings?.DefaultMinorAge ?? settings?.DefaultRecMinorAge)
        : (settings?.DefaultRecMinorAge ?? settings?.DefaultMinorAge);
    const threshold = minorAge ?? settings?.OfAge ?? null;
    if (dateOfBirth && threshold && ageInYears(dateOfBirth, new Date()) < threshold) {
      return TYPED_MINOR_MESSAGE;
    }

    /**
     * Enforced unconditionally — independent of `RequireValidDL` (the licence-only gate below) and
     * of `MMJIdExpirationDateCheck` (the *checkout* rule, never read on this screen at all). The
     * check-in this entry feeds enforces MMJ expiry regardless of either flag — the same reason
     * Register's typed entry enforces it unconditionally — so a stale card must not pass this
     * preflight only to have the check-in refuse it afterwards: the half-made record this gate
     * exists to prevent. `isMmjIdExpired` is the shared implementation Register's own check-in
     * boundary uses, extracted to `@dutchie-pos/utils` so both surfaces read the same semantics for
     * `ThroughMMJIdExpiry` — off (the default) means the expiration day itself is invalid, on means
     * valid through that day. A blank or unparseable expiration is deliberately not treated as
     * expired here either: the first stays governed by the existing required-field policy (the
     * expiration is not one of the four required typed fields), and the second is the same legacy
     * stance `isMmjIdExpired` already takes for Register.
     */
    if (
      documentType === 'MedicalCard' &&
      isMmjIdExpired(values.ExpirationDate, Boolean(location?.Features?.ThroughMMJIdExpiry))
    ) {
      return TYPED_EXPIRED_MEDICAL_MESSAGE;
    }

    if (location?.Features?.RequireValidDL) {
      /**
       * A medical card is not a licence, and this entry's create body has no licence slot at all -
       * no `DriversLicenseId`, no `DLExpirationDate`, which its own test asserts. So the card's
       * expiration is not evidence of the thing this location asked for, and accepting it would
       * admit a guest on a document that says nothing about their licence. Register's typed entry
       * refuses the same configuration for the same reason, and refers staff to the counter.
       */
      if (documentType === 'MedicalCard') {
        return TYPED_INVALID_ID_MESSAGE;
      }

      const expiration = toLocalDate(values.ExpirationDate);
      const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));
      // Absent counts as invalid: this location asked for a current ID, and an undated one is not
      // evidence of anything. The same answer the register's own check-in gives.
      if (!expiration || expiration.getTime() < startOfToday.getTime()) {
        return TYPED_INVALID_ID_MESSAGE;
      }
    }

    return null;
  };

  /**
   * Creates the typed profile, then checks it in — and holds the new id from the moment it is known.
   *
   * The second write can fail on its own, and a retry that created again would leave two profiles
   * for one person. So a retry from here only checks in, exactly as the decision path's create does.
   */
  const createAndCheckInTyped = async (body: ReturnType<typeof toTypedGuestCreateBody>, generation: number) => {
    let createdId = createdCustomerId.current;
    try {
      if (!createdId) {
        const [created] = await createTypedGuest.mutateAsync(body);
        if (!created?.Guest_id) {
          dispatch(errorNotification(CREATE_FAILURE_MESSAGE));
          return;
        }

        createdId = created.Guest_id;
        // Scoped to this attempt: held across a Scan again, the next customer's check-in would land
        // on this profile under their details.
        if (isCurrentDecision(generation)) {
          createdCustomerId.current = createdId;
        }
      }

      /**
       * Abandoned: staff scanned again before this create's automatic follow-up check-in started.
       * `completeWith` below already withholds the navigation for a stale decision, but that guard
       * ran only *after* `checkIn` — the real POST — so the request still went out, and `finish`
       * dispatches its confirmation toast unconditionally before it withholds the navigation, so
       * the stale attempt also toasted. Checked here, before the call, so none of that happens.
       *
       * Unlike the decision path's explicit per-candidate Check in / Create taps, this follow-up
       * check-in was never its own ask: staff asked for a create, or a create-then-check-in, and
       * scan again withdraws that before the automatic half runs.
       */
      if (!isCurrentDecision(generation)) {
        return;
      }

      await checkIn(createdId, body.MJStateIDNo ?? null);
      /**
       * A profile that did not exist a moment ago, so the created-profile default applies: it cannot
       * already have a photo unless a record explicitly says so, and `v2/guest/create` answers with
       * an id and no presence field at all. Called rather than assumed, so the rule stays in one
       * place across both surfaces.
       */
      completeWith(
        isCurrentDecision(generation),
        newProfileNeedsFrontIdPhoto(null) ? createdId : null,
        'Customer created and checked in',
      );
    } catch (error) {
      // `createdId` is set before the check-in runs, so this tells a failed create from a failed
      // check-in — and never tells staff a profile could not be created when it already exists.
      dispatch(errorNotification(message(error, createdId ? CHECK_IN_FAILURE_MESSAGE : CREATE_FAILURE_MESSAGE)));

      /**
       * The profile exists, and a retry must stay fixed to it — the id, and the MJ id this exact
       * create body carried, never whatever staff might type next. Swapped in for the form rather
       * than left beside it, the same way a genuine duplicate match replaces it: with the editable
       * fields gone, there is nothing left for a retry to diverge from.
       */
      if (createdId && isMounted.current && isCurrentDecision(generation)) {
        const retryId = createdId;
        setStep((current) =>
          current.name === 'completion'
            ? { ...current, createdPendingCheckIn: { Guest_id: retryId, MJStateIDNo: body.MJStateIDNo ?? null } }
            : current,
        );
      }
    }
  };

  /**
   * Staff looked at the retry panel for a profile this screen already created and asked to try the
   * check-in again. The id and the MJ id both come from `step.createdPendingCheckIn`, fixed at the
   * moment the create landed — never from `step.values`, which this stage does not even render an
   * editable copy of any more.
   */
  const handleRetryCreatedCheckIn = () => {
    if (step.name !== 'completion' || isSubmitting) {
      return;
    }

    const pending = step.createdPendingCheckIn;
    if (!pending) {
      return;
    }

    const generation = decisionGeneration.current;

    void (async () => {
      try {
        await checkIn(pending.Guest_id, pending.MJStateIDNo);
        // A profile this screen created, not a genuine duplicate match, so the created-profile photo
        // default applies here too — this is that same create's retry.
        completeWith(
          isCurrentDecision(generation),
          newProfileNeedsFrontIdPhoto(null) ? pending.Guest_id : null,
          'Customer created and checked in',
        );
      } catch (error) {
        dispatch(errorNotification(message(error, CHECK_IN_FAILURE_MESSAGE)));
      }
    })();
  };

  /**
   * The typed entry, in the only order that is safe: refuse what is refusable, ask whether this
   * person already has a profile, and only then write one.
   *
   * The duplicate check is mandatory and its failure is not "no duplicate": a create after an
   * unanswered check could be the second profile for a customer who already has one, which carries
   * its own purchase history and compliance record.
   */
  const handleSubmitTyped = () => {
    if (
      step.name !== 'completion' ||
      step.match ||
      step.createdPendingCheckIn ||
      isSubmitting ||
      isSubmittingRef.current
    ) {
      return;
    }
    /**
     * Acquired synchronously, before anything else — including the refusal computation, which can
     * be reached twice in one tick just as easily as the writes below.
     *
     * `isSubmitting` is derived from the four mutations' own `isPending` flags, and those do not
     * flip until React processes the state update each mutation schedules. Two taps landing in the
     * same tick both read `isSubmitting === false` and each would reach the duplicate check and the
     * create — the second profile this whole path exists to prevent. The button is disabled by that
     * same state, so an ordinary double click is already stopped by React flushing between the two;
     * a key and a tap together are not, and neither is any future caller. The state stays, because
     * it is what the button's `disabled` prop reads.
     */
    isSubmittingRef.current = true;
    const release = () => {
      isSubmittingRef.current = false;
    };

    const { values } = step;
    if (missingRequiredFields(values).length > 0) {
      release();
      return;
    }

    const customerTypeId = selectCustomerTypeId({
      documentType,
      customerTypes,
      defaultCustomerTypeId: location?.Settings?.DefaultCustomerTypeId,
    });
    const refusal = typedEntryRefusal(values, customerTypeId);
    if (refusal) {
      dispatch(errorNotification(refusal));
      release();
      return;
    }

    const body = toTypedGuestCreateBody({ values, documentType, customerTypeId });
    const generation = decisionGeneration.current;

    void (async () => {
      try {
        await submitTyped(body, generation);
      } finally {
        release();
      }
    })();
  };

  /** The duplicate check, then the create/check-in — split out so `handleSubmitTyped` can guarantee the lock releases on every exit. */
  const submitTyped = async (body: ReturnType<typeof toTypedGuestCreateBody>, generation: number) => {
    let match: DuplicateGuestMatch | null;
    try {
      match = await checkDuplicateGuest.mutateAsync(body);
    } catch {
      dispatch(errorNotification(DUPLICATE_CHECK_FAILURE_MESSAGE));
      return;
    }

    // Left mid-request: nothing has been written yet, so the right answer is to write nothing.
    if (!isMounted.current || !isCurrentDecision(generation)) {
      return;
    }

    // Archived is not a customer anyone can check in, so it is neither offered nor a blocker.
    if (match?.Guest_id && match.status !== 'Archived') {
      setStep((current) => (current.name === 'completion' ? { ...current, match } : current));
      return;
    }

    await createAndCheckInTyped(body, generation);
  };

  /** Staff looked at the profile the duplicate check found and said it is the same person. */
  const handleCheckInTypedMatch = () => {
    if (step.name !== 'completion' || isSubmitting) {
      return;
    }

    const guestId = step.match?.Guest_id;
    const match = step.match;
    if (!guestId || !match) {
      return;
    }

    const generation = decisionGeneration.current;

    void (async () => {
      try {
        await checkIn(guestId, match.MJStateIDNo ?? null);
        /**
         * An existing profile, so the existing-profile rule applies: asked about only when the
         * duplicate check said it has no photo. Unknown presence is not a missing photo — prompting
         * anyway would overwrite a good scan-time image and cost staff a step on every return visit.
         */
        completeWith(
          isCurrentDecision(generation),
          needsFrontIdPhoto({ HasIdPhoto: match.HasIdPhoto }) ? guestId : null,
          'Customer checked in',
        );
      } catch (error) {
        dispatch(errorNotification(message(error, CHECK_IN_FAILURE_MESSAGE)));
      }
    })();
  };

  const checkIn = async (customerId: number, mjStateId: string | null) => {
    await checkInGuest.mutateAsync({
      AcctId: customerId,
      MJStateIDNo: mjStateId ?? undefined,
      Register: registerId,
    });
  };

  const handleCheckIn = (customer: ScanValidatedCustomer) => {
    const generation = decisionGeneration.current;
    // Read now, not after the await: the step this candidate came from is what says whether the
    // pipeline created it, and by the time the check-in settles the step may have moved on.
    const entry = step.name === 'decision' ? step.entry : null;

    void (async () => {
      try {
        await checkIn(customer.Id, customer.MJStateIdNumber);
        // Asked of the candidate staff actually picked, which is the one being checked in. An
        // existing profile prompts only on an explicit `HasIdPhoto: false` — unknown presence is not
        // a missing photo, and asking anyway would overwrite a good scan-time image and cost staff a
        // step on every return visit. The profile the pipeline created, offered here as a candidate
        // under `CreatedCustomerId`, is the opposite case: silence means there cannot be one yet.
        completeWith(
          isCurrentDecision(generation),
          needsFrontIdPhotoForCandidate(entry, customer) ? customer.Id : null,
          'Customer checked in',
        );
      } catch (error) {
        dispatch(errorNotification(message(error, CHECK_IN_FAILURE_MESSAGE)));
      }
    })();
  };

  // Creates the profile, then checks it in. The second write can fail on its own, which used to leave
  // the step offering Create again for a person who now has a profile — so the id is held from the
  // moment it is known and a retry only checks in. Register guards this in `useScanDetailsPopup`.
  const handleCreate = (customer: ScanValidatedCustomer) => {
    const generation = decisionGeneration.current;

    void (async () => {
      let createdId = createdCustomerId.current;
      try {
        if (!createdId) {
          const [created] = await createGuest.mutateAsync(customer);
          if (!created?.Guest_id) {
            dispatch(errorNotification(CREATE_FAILURE_MESSAGE));
            return;
          }

          createdId = created.Guest_id;
          // Remembered only for the decision it belongs to: held across one, the next decision's
          // Create would check in *this* profile under that customer's details.
          if (isCurrentDecision(generation)) {
            createdCustomerId.current = createdId;
          }
        }

        await checkIn(createdId, customer.MJStateIdNumber);
        /**
         * A profile that did not exist a moment ago, so F4's created-profile default applies: it
         * cannot already have a photo unless a record explicitly says so. `v2/guest/create` answers
         * with an id and no presence field at all, which is exactly the case that default is written
         * for. Called rather than assumed, so the rule stays in one place — and note the asymmetry
         * with the check-in path above: silence about an existing profile is unknown and withholds,
         * silence about a profile this screen just created means there cannot be one yet.
         */
        completeWith(
          isCurrentDecision(generation),
          newProfileNeedsFrontIdPhoto(null) ? createdId : null,
          'Customer created and checked in',
        );
      } catch (error) {
        // Set before the check-in ran, so this tells a failed create from a failed check-in.
        dispatch(errorNotification(message(error, createdId ? CHECK_IN_FAILURE_MESSAGE : CREATE_FAILURE_MESSAGE)));
      }
    })();
  };

  if (isLoadingLocation) {
    return <ScreenLoader top={headerHeight} visible />;
  }
  if (!isCameraIdScanEnabled) {
    return <Navigate replace to='/paths' />;
  }
  // Entering is refused without a register, so this is gated before anything is scanned.
  if (!registerId) {
    return <Navigate replace to='/registers' />;
  }

  return (
    <ContentLayout>
      <ContentTitle onGoBack={() => navigate('/registers')}>Check in</ContentTitle>
      {step.name === 'capture' && (
        <CaptureStep
          documentType={documentType}
          isEntering={cameraScan.isPending}
          onCameraError={(text) => dispatch(errorNotification(text))}
          onDocumentTypeChange={setDocumentType}
          onScanResult={handleScanResult}
          onUploadResult={handleUploadResult}
        />
      )}
      {step.name === 'completion' && (
        <CompletionStep
          createdPendingCheckIn={step.createdPendingCheckIn}
          isSubmitting={isSubmitting}
          match={step.match}
          values={step.values}
          onChange={handleTypedFieldChange}
          onCheckInMatch={handleCheckInTypedMatch}
          onRetake={returnToCapture}
          onRetryCreatedCheckIn={handleRetryCreatedCheckIn}
          onSubmit={handleSubmitTyped}
        />
      )}
      {step.name === 'frontPhoto' && (
        <FrontPhotoStep isSaving={step.isSaving} onCapture={handleFrontPhoto} onSkip={handleSkipFrontPhoto} />
      )}
      {step.name === 'decision' && (
        <DecisionStep
          candidates={step.candidates}
          createFrom={step.createFrom}
          identified={step.identified}
          features={location?.Features}
          isSubmitting={isSubmitting}
          onCheckIn={handleCheckIn}
          onCreate={handleCreate}
          onStartOver={returnToCapture}
        />
      )}
    </ContentLayout>
  );
};
